/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable promise/catch-or-return */

// # Please ensure understanding of the pricing page public setup, documented here:
// # https://product.hubteam.com/docs/growth-cbx-handbook/docs/dotcom/overview.html
// # before making changes to this file or merging in mothership changes

import { createBrowserHistory } from 'history';
import { render } from 'react-dom';
import UIApplication from 'UIComponents/app/UIApplication';

// I18n Configuration
import createPublicI18nProvider from 'I18n/init/providers/createPublicI18nProvider';
import registerAllData from 'I18n/init/register/sync/registerAllData';
import { initAppTranslations } from 'I18n/init/setup/AppInit';
import lang from 'i2l?query=sporks!../lang/en.lyaml';

// Import extra I18n data
import 'I18n/init/data/baseLocales';
import 'I18n/init/data/currency';
import 'I18n/init/data/publicLocales';

// Rhumb
import { RhumbProvider } from 'react-rhumb';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import rhumbConfig from '../rhumb-config.yaml';

// Redux
import { Provider } from 'react-redux';
import { startApp } from './actions/AppActions';
import configureStore from './utils/configureStore';

// Configure the router
import { Router } from 'react-router-dom';
import Routes from './Routes';

// import Raven from 'Raven';

import { setPrimaryTracker } from 'usage-tracker-container';
import { storeEnvironmentConfig } from './actions/ConfigActions';
import isThenable from 'hs-promise-utils/isThenable';
import { AllRoutes, MARKETING } from 'pricing-pages-lib/constants/PricingPageRoutes';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import safeLocalStorage from 'pricing-pages-lib/utils/safeLocalStorage';
import { getAbsolutePricingPageUrl } from 'pricing-pages-lib/utils/urls';
import { fetchPublicProducts } from './actions/ProductsActions';
import { fetchPublicMetaData } from './actions/PublicMetaDataActions';
import { fetchPurchaseMotionData } from './actions/PurchaseMotionActions';
import { PublicI18nContext } from './context/I18nContext';
import IsMobileContextProvider from './context/IsMobileContext';
import { Metrics } from './utils/metrics';
import { getProductLevelFromPath } from './utils/urls/publicUtils';

// const whenIdle = (cb: IdleRequestCallback) => {
//   if (window.hasOwnProperty('requestIdleCallback')) {
//     window.requestIdleCallback(cb);
//   } else {
//     setTimeout(cb, 0);
//   }
// };
import { jsx as _jsx } from "react/jsx-runtime";
const store = configureStore();
const {
  dispatch
} = store;
export default class App {
  constructor(config) {
    this.config = config;
  }

  // Kicks off the app. This is intentionally not in the constructor.
  start() {
    dispatch(startApp());
    setPrimaryTracker(this.config.tracker); // TODO: remove the any type

    // Set up I18n. Docs in: https://product.hubteam.com/docs/frontend/docs/i18n/new-i18n-setup.html
    let I18nProvider;
    if (window.I18N_MANUAL_LANG) {
      I18nProvider = createPublicI18nProvider({
        manuallySetLocale: true
      });
      I18nProvider.setLocale({
        locale: window.I18N_MANUAL_LANG,
        langEnabled: true
      });
    } else {
      I18nProvider = createPublicI18nProvider();
    }
    // const ExperimentalI18nProvider = createExperimentI18nProvider({
    //   client: inAppTreatmentsClient,
    // });

    const langAvailable = initAppTranslations([I18nProvider.register(lang), registerAllData(I18nProvider)]);
    const history = createBrowserHistory({
      basename: this.config.rootUrl
    });
    const bootstrapBlockingPromises = this.fetchBootstrapBlockers(history);
    Promise.all([...bootstrapBlockingPromises, langAvailable]).then(() => {
      this.redirectNonProxied();
      this.startRouter(history, I18nProvider);
    }).catch(__error => {
      langAvailable.then(() => {
        // We chain our bootstrapping code with this catch
        // so we can show an Error screen rather than just a blank page
        // A side effect of this is that any errors that bubble up here
        // would be swallowed up unless we re-throw.
        this.startRouter(history, I18nProvider, true);
        // Raven.captureException(new Error('Failed to bootstrap public app'), {
        //   extra: { error },
        // });
      });
    });
  }
  fetchBootstrapBlockers(history) {
    if (!history) {
      throw new Error(`cannot bootstrap without history`);
    }
    const promises = [];
    const blockingRequests = this.config.blockingRequests || [];
    blockingRequests.forEach(blockingCall => {
      /*
       * Potential return value(s) of a blockingCall() include:
       *
       *   1. A promise
       *   2. A thunk action creator
       */
      let promise = blockingCall();

      // Non-promises assumed to be thunk action creators
      if (!isThenable(promise)) {
        // Wrap in dispatch for thunk middleware to take effect
        promise = dispatch(promise);
      }
      promises.push(promise);
    });
    promises.push(dispatch(storeEnvironmentConfig(this.config)));
    return promises;
  }
  fetchPricingAndPurchasingData() {
    const promises = [];
    promises.push(dispatch(fetchPurchaseMotionData({
      getPurchaseMotions: this.config.getPurchaseMotions,
      isPublic: true
    })).catch(console.error));
    promises.push(dispatch(fetchPublicProducts()).catch(console.error));
    promises.push(dispatch(fetchPublicMetaData()).catch(console.error));
    return promises;
  }
  addChatTrackingEventHandler() {
    window.HubSpotConversations.on('conversationStarted', __payload => {
      // Assumes experiment treatments are fetched by now. May not
      // be 100% correct for lazy loaded experiments, but probably
      // fine since this requires user interaction to fire and gives
      // enough time
      // TODO: Unblock passing experiment arrays into chat widget tracking
      // let formattedExperimentArray = getFormattedExperimentArray(
      //   store.getState().experimentTreatments
      // );
      getUsageTracker().track('conversations', {
        action: 'conversation-started',
        // formattedExperimentArray,
        conversationId: __payload.conversation.conversationId
      });
      Metrics.counter('live-chat-interaction').increment();
    });
  }
  setupChatTracking() {
    if (window.HubSpotConversations) {
      this.addChatTrackingEventHandler();
      Metrics.counter('live-chat-loaded').increment();
    } else {
      window.hsConversationsOnReady = [() => {
        this.addChatTrackingEventHandler();
        Metrics.counter('live-chat-loaded').increment();
      }];
    }
  }
  redirectNonProxied() {
    const team = safeLocalStorage.getItem('hs-team');
    const isOnApp = !!window.location.hostname.match(/^(app|app-eu1)\.hubspot(qa)?\.com/);
    const isQA = window.location.hostname.indexOf('qa') >= 0;
    const productLine = window.location.pathname.split('/').filter(_ => !!_).find(slug => {
      return AllRoutes.includes(slug);
    }) || MARKETING;
    const productLevel = getProductLevelFromPath(window.location.pathname);
    const newUrl = `${getAbsolutePricingPageUrl({
      slug: productLine,
      isAppAuth: false,
      isQA,
      productLevel
    })}${window.location.search}`;
    if (isOnApp && team !== 'cbx') {
      window.location.href = newUrl;
      // Commenting out for ATs until we get proper QA setup
      // Raven.captureException(
      //   new Error('Unexpected visitor on public app.hubspot.com')
      // );
    }
  }
  startRouter(history, i18nLoader, __hasError = false) {
    this.fetchPricingAndPurchasingData();
    this.setupChatTracking();
    render( /*#__PURE__*/_jsx(UIApplication, {
      name: "pricing-pages-ui",
      children: /*#__PURE__*/_jsx(Provider, {
        store: store,
        children: /*#__PURE__*/_jsx(PublicI18nContext.Provider, {
          value: {
            i18nLoader: i18nLoader
          },
          children: /*#__PURE__*/_jsx(RhumbProvider
          // TODO: Update type here
          , {
            history: historyAdapter(history)
            // TODO: Update type here
            ,
            config: rhumbConfig,
            children: /*#__PURE__*/_jsx(IsMobileContextProvider, {
              children: /*#__PURE__*/_jsx(Router, {
                history: history // TODO: Update type here
                ,
                children: /*#__PURE__*/_jsx(Routes, {})
              })
            })
          })
        })
      })
    }), document.getElementsByClassName('page')[0]);
  }
}